import type React from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../tailgrids/core/resizable.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { cn } from '../../utils/cn.js';

interface SplitPaneProps {
  left: React.ReactNode;
  right: React.ReactNode;
  /** Remembers the user's split in localStorage under this id. */
  storageKey: string;
  /** Left panel share in percent. */
  defaultLeft?: number;
  minLeft?: number;
  maxLeft?: number;
  /** Names the drag handle for screen readers. */
  handleLabel?: string;
  className?: string;
}

/**
 * Two resizable panels side by side (NextAdmin resizable on react-resizable-panels): drag or use the arrow keys on the
 * handle; the split is remembered. Below 1100px the panels stack.
 */
export function SplitPane({
  left,
  right,
  storageKey,
  defaultLeft = 70,
  minLeft = 30,
  maxLeft = 85,
  handleLabel = 'Resize panels',
  className,
}: SplitPaneProps) {
  const narrow = useMediaQuery('(max-width: 1099px)');
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({ id: storageKey, panelIds: ['left', 'right'] });

  if (narrow) {
    return (
      <div className={cn('flex flex-col gap-4', className)}>
        <section className="min-h-[560px] min-w-0">{left}</section>
        <aside className="min-w-0">{right}</aside>
      </div>
    );
  }

  return (
    <ResizablePanelGroup
      orientation="horizontal"
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
      className={cn('min-h-[680px]', className)}
    >
      <ResizablePanel id="left" defaultSize={`${defaultLeft}%`} minSize={`${minLeft}%`} maxSize={`${maxLeft}%`}>
        <section className="h-full min-w-0 pr-2">{left}</section>
      </ResizablePanel>
      <ResizableHandle withHandle aria-label={handleLabel} />
      <ResizablePanel id="right" minSize={`${100 - maxLeft}%`}>
        <aside className="h-full min-w-0 overflow-y-auto pl-2">{right}</aside>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
